import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  colorTokens,
  getShadow,
  getSpacing,
  getTypography,
} from "../../../../../designTokens";
import type {
  OverviewExportDeliveryMode,
  OverviewExportRecord,
  OverviewExportStatus,
} from "../../../../../Apis/overviewExportTypes";
import { pollExportStatus } from "../../../../../utils/pollExportStatus";
import { track } from "../../../../../analytics";
import { formatExportExpiryDate } from "../../../../../utils/buildOverviewExportPayloads";
import { ExportModalCloseButton } from "./ExportModalCloseButton";

interface ExportStatusModalProps {
  isOpen: boolean;
  exportId: string | null;
  /** Analytics (W2-O): the export entity enum. When absent (uninstrumented
   * mounts), export_completed is not fired. */
  analyticsEntity?: "opportunities" | "patients" | "tasks";
  getStatus?: (
    exportId: string
  ) => Promise<{ success: boolean; export?: OverviewExportRecord }>;
  /** When set, the parent owns polling (CAR-2738 global host). */
  status?: OverviewExportStatus;
  processedCount?: number;
  completedRecord?: OverviewExportRecord | null;
  errorMessage?: string | null;
  isPolling?: boolean;
  entityLabel?: string;
  recipientEmail?: string | null;
  columnCount?: number;
  deliveryMode?: OverviewExportDeliveryMode | null;
  canDownloadInApp?: boolean;
  onClose: () => void;
  onMinimize?: () => void;
  onViewHistory?: () => void;
  idPrefix?: string;
}

function statusMessage(
  status: OverviewExportStatus,
  processedCount?: number
): string {
  switch (status) {
    case "QUEUED":
      return "Preparing your export...";
    case "PROCESSING":
      return processedCount != null
        ? `Processing ${processedCount.toLocaleString()} records...`
        : "Processing records...";
    case "COMPLETED":
      return "Your export is ready.";
    case "FAILED":
      return "Export failed.";
    default:
      return "Preparing your export...";
  }
}

const minimizeButtonStyle: React.CSSProperties = {
  width: "32px",
  height: "32px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  color: colorTokens["Text/Light Grey"],
  padding: 0,
};

export const ExportStatusModal: React.FC<ExportStatusModalProps> = ({
  isOpen,
  exportId,
  getStatus,
  analyticsEntity,
  status: statusProp,
  processedCount: processedCountProp,
  completedRecord: completedRecordProp,
  errorMessage: errorMessageProp,
  isPolling: isPollingProp,
  entityLabel = "data",
  recipientEmail,
  columnCount,
  deliveryMode,
  canDownloadInApp = false,
  onClose,
  onMinimize,
  onViewHistory,
  idPrefix = "export-status-modal",
}) => {
  const parentOwnsStatus = statusProp !== undefined;
  const [status, setStatus] = useState<OverviewExportStatus>("QUEUED");
  const [processedCount, setProcessedCount] = useState<number | undefined>();
  const [completedRecord, setCompletedRecord] =
    useState<OverviewExportRecord | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** Analytics: export_completed must fire ONCE per export — the poll effect
   * restarts when getStatus changes identity, and superseded runs may still
   * resolve. Keyed by exportId so a NEW export in the same modal still fires. */
  const completedTrackedForExportIdRef = useRef<string | null>(null);
  const [polling, setPolling] = useState(false);

  const reset = useCallback(() => {
    setStatus("QUEUED");
    setProcessedCount(undefined);
    setCompletedRecord(null);
    setErrorMessage(null);
    setPolling(false);
  }, []);

  useEffect(() => {
    if (parentOwnsStatus) return;
    if (!isOpen || !exportId || !getStatus) {
      reset();
      return;
    }

    let cancelled = false;
    setPolling(true);
    setErrorMessage(null);
    setCompletedRecord(null);
    setStatus("QUEUED");

    void (async () => {
      try {
        const record = await pollExportStatus({
          exportId,
          getStatus,
          onProgress: (s, count) => {
            if (cancelled) return;
            setStatus(s);
            setProcessedCount(count);
          },
        });
        if (!cancelled) {
          setCompletedRecord(record);
          setStatus("COMPLETED");
          // Analytics (W2-O, A.13): plain track — the funnel completed at
          // submit; this is the async outcome, possibly long after the
          // wizard. Fired-ref: exactly once per exportId across poll restarts.
          if (
            analyticsEntity &&
            completedTrackedForExportIdRef.current !== exportId
          ) {
            completedTrackedForExportIdRef.current = exportId;
            try {
              track("export_completed", {
                entity: analyticsEntity,
                export_id: exportId,
                status: "COMPLETED",
              });
            } catch {
              // fail-open
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Export failed";
          setErrorMessage(msg);
          setStatus("FAILED");
          if (
            analyticsEntity &&
            completedTrackedForExportIdRef.current !== exportId
          ) {
            completedTrackedForExportIdRef.current = exportId;
            try {
              track("export_completed", {
                entity: analyticsEntity,
                export_id: exportId,
                status: "FAILED",
              });
            } catch {
              // fail-open
            }
          }
        }
      } finally {
        if (!cancelled) setPolling(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, exportId, getStatus, reset, analyticsEntity, parentOwnsStatus]);

  if (!isOpen) return null;

  const resolvedStatus = parentOwnsStatus ? statusProp : status;
  const resolvedProcessedCount = parentOwnsStatus
    ? processedCountProp
    : processedCount;
  const resolvedCompletedRecord = parentOwnsStatus
    ? (completedRecordProp ?? null)
    : completedRecord;
  const resolvedError = parentOwnsStatus
    ? (errorMessageProp ?? null)
    : errorMessage;
  const resolvedPolling = parentOwnsStatus ? Boolean(isPollingProp) : polling;

  const isInAppDelivery = deliveryMode === "IN_APP";
  const isExportComplete = resolvedStatus === "COMPLETED";
  const showDownload =
    canDownloadInApp &&
    isExportComplete &&
    Boolean(resolvedCompletedRecord?.presignedUrl);
  const isComplete = isExportComplete;
  const isFailed = resolvedStatus === "FAILED" || Boolean(resolvedError);
  const isInProgress = !isComplete && !isFailed;
  const showMinimize = Boolean(onMinimize) && isInProgress;

  const outlineButtonStyle = (disabled?: boolean): React.CSSProperties => ({
    width: "100%",
    height: "40px",
    borderRadius: getSpacing("8px"),
    border: `1px solid ${colorTokens["Primary/Default"]}`,
    backgroundColor: colorTokens["Background/White"],
    color: colorTokens["Primary/Default"],
    ...getTypography("Links/Btn Text"),
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: getSpacing("16px"),
      }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (isInProgress && onMinimize) {
          onMinimize();
          return;
        }
        onClose();
      }}
      role="presentation"
      id={`${idPrefix}-backdrop`}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          backgroundColor: colorTokens["Background/White"],
          borderRadius: getSpacing("16px"),
          boxShadow: getShadow("Shadow"),
          padding: getSpacing("24px"),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: getSpacing("16px"),
          boxSizing: "border-box",
          position: "relative",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${idPrefix}-title`}
        onClick={(e) => e.stopPropagation()}
        id={`${idPrefix}-container`}
      >
        <div
          style={{
            position: "absolute",
            top: getSpacing("12px"),
            right: getSpacing("12px"),
            display: "flex",
            alignItems: "center",
            gap: getSpacing("4px"),
          }}
        >
          {showMinimize ? (
            <button
              type="button"
              onClick={onMinimize}
              aria-label="Minimize export progress"
              title="Minimize"
              id={`${idPrefix}-minimize-icon`}
              style={minimizeButtonStyle}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8h10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
          <ExportModalCloseButton
            onClick={onClose}
            id={`${idPrefix}-close-icon`}
          />
        </div>

        <div
          style={{
            ...getTypography("Headings/H3"),
            color: colorTokens["Text/Black"],
            textAlign: "center",
            margin: 0,
            paddingRight: getSpacing("48px"),
          }}
          id={`${idPrefix}-title`}
        >
          {isComplete
            ? "Export Ready"
            : isFailed
              ? "Export Failed"
              : "Export In Progress"}
        </div>

        <div
          style={{
            ...getTypography("Body/Medium"),
            color: colorTokens["Text/Grey"],
            textAlign: "center",
            width: "100%",
          }}
        >
          {isFailed
            ? resolvedError
            : isComplete
              ? columnCount != null
                ? isInAppDelivery
                  ? `Your ${entityLabel} export with ${columnCount} column${columnCount === 1 ? "" : "s"} is ready to download.`
                  : recipientEmail
                    ? `Your ${entityLabel} export with ${columnCount} column${columnCount === 1 ? "" : "s"} is ready. It's been sent to ${recipientEmail}.`
                    : `Your ${entityLabel} export with ${columnCount} column${columnCount === 1 ? "" : "s"} is ready.`
                : isInAppDelivery
                  ? `Your ${entityLabel} export is ready to download.`
                  : recipientEmail
                    ? `Your ${entityLabel} export is ready. It's been sent to ${recipientEmail}.`
                    : `Your ${entityLabel} export is ready.`
              : statusMessage(resolvedStatus, resolvedProcessedCount)}
        </div>

        {isInProgress && (resolvedPolling || parentOwnsStatus) && (
          <div
            style={{
              width: "32px",
              height: "32px",
              border: `3px solid ${colorTokens["Borders/Light"]}`,
              borderTopColor: colorTokens["Primary/Default"],
              borderRadius: "50%",
              animation: "export-spin 0.8s linear infinite",
            }}
            aria-hidden="true"
          />
        )}

        {showDownload && resolvedCompletedRecord?.presignedUrlExpiresAt && (
          <div
            style={{
              ...getTypography("Body/Small"),
              color: colorTokens["Text/Light Grey"],
              textAlign: "center",
            }}
          >
            Download link expires:{" "}
            {formatExportExpiryDate(
              resolvedCompletedRecord.presignedUrlExpiresAt
            )}
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: getSpacing("8px"),
            width: "100%",
          }}
        >
          {showDownload && resolvedCompletedRecord?.presignedUrl && (
            <a
              href={resolvedCompletedRecord.presignedUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: "100%",
                height: "40px",
                borderRadius: getSpacing("8px"),
                border: "none",
                backgroundColor: colorTokens["Primary/Default"],
                color: colorTokens["Background/White"],
                ...getTypography("Links/Btn Text"),
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                boxSizing: "border-box",
              }}
              id={`${idPrefix}-download`}
            >
              Download CSV
            </a>
          )}

          {onViewHistory && (
            <button
              type="button"
              onClick={onViewHistory}
              style={outlineButtonStyle()}
              id={`${idPrefix}-history`}
            >
              View export history
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            style={
              isComplete || isFailed
                ? {
                    ...outlineButtonStyle(),
                    border: `1px solid ${colorTokens["Borders/Light"]}`,
                    color: colorTokens["Text/Black"],
                  }
                : outlineButtonStyle()
            }
            id={`${idPrefix}-done`}
          >
            {isComplete || isFailed ? "Done" : "Close"}
          </button>
        </div>
      </div>
      <style>{`@keyframes export-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
